import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CategoryCount,
  DemoTestTotals,
  DistributorComparison,
  DistributorSalesData,
  DistributorWithCountry,
  MultiAccountDevices,
  OrgBreakdown,
  OrgTestCountAnalytics,
  TestCountAnalytics,
} from "types/analyticsTypes";

interface DateRangeParams {
  startDate?: string;
  endDate?: string;
}

export interface AnalyticsState {
  distributorSales: DistributorSalesData | null;
  distributorOrgTestCounts: OrgTestCountAnalytics[] | null;
  distributorCategoryDistribution: CategoryCount[] | null;
  demoTestTotals: DemoTestTotals | null;
  distributorComparison: DistributorComparison | null;
  multiAccountDevices: MultiAccountDevices | null;
  orgBreakdowns: Record<string, OrgBreakdown>;
  orgTestCounts: OrgTestCountAnalytics | null;
  singleDeviceTestCounts: TestCountAnalytics | null;
  allDistributorIds?: DistributorWithCountry[] | null;
}

export const initialState: AnalyticsState = {
  distributorSales: null,
  distributorOrgTestCounts: null,
  distributorCategoryDistribution: null,
  demoTestTotals: null,
  distributorComparison: null,
  multiAccountDevices: null,
  orgBreakdowns: {},
  orgTestCounts: null,
  singleDeviceTestCounts: null,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    resetAnalyticsState: () => {
      return initialState;
    },
    requestDistributorSales: (
      _state,
      _action: PayloadAction<{ distributorId?: string } & DateRangeParams>,
    ) => {},
    requestDistributorOrgTestCounts: (
      _state,
      _action: PayloadAction<{ distributorId?: string } & DateRangeParams>,
    ) => {},
    requestCategoryDistribution: (
      _state,
      _action: PayloadAction<{ distributorId?: string } & DateRangeParams>,
    ) => {},
    requestDemoTestTotals: (_state, _action: PayloadAction<{ distributorId?: string }>) => {},
    requestDistributorComparison: (_state, _action: PayloadAction<DateRangeParams>) => {},
    requestMultiAccountDevices: (_state, _action: PayloadAction<{ distributorId?: string }>) => {},
    requestOrgBreakdown: (
      _state,
      _action: PayloadAction<{ orgId: string } & DateRangeParams>,
    ) => {},
    requestOrgTestCounts: (_state, _action: PayloadAction<{ orgId?: string }>) => {},
    requestSingleDeviceTestCounts: (
      _state,
      _action: PayloadAction<{ deviceId: string; orgId: string }>,
    ) => {},
    requestAllDistributorIds: (_state) => {},
    setDistributorSales: (state, action: PayloadAction<DistributorSalesData | null>) => {
      state.distributorSales = action.payload;
    },
    setDistributorOrgTestCounts: (state, action: PayloadAction<OrgTestCountAnalytics[] | null>) => {
      state.distributorOrgTestCounts = action.payload;
    },
    setCategoryDistribution: (state, action: PayloadAction<CategoryCount[] | null>) => {
      state.distributorCategoryDistribution = action.payload;
    },
    setDemoTestTotals: (state, action: PayloadAction<DemoTestTotals | null>) => {
      state.demoTestTotals = action.payload;
    },
    setDistributorComparison: (state, action: PayloadAction<DistributorComparison | null>) => {
      state.distributorComparison = action.payload;
    },
    setMultiAccountDevices: (state, action: PayloadAction<MultiAccountDevices | null>) => {
      state.multiAccountDevices = action.payload;
    },
    setOrgBreakdown: (state, action: PayloadAction<{ orgId: string; breakdown: OrgBreakdown }>) => {
      state.orgBreakdowns[action.payload.orgId] = action.payload.breakdown;
    },
    clearOrgBreakdowns: (state) => {
      state.orgBreakdowns = {};
    },
    setOrgTestCounts: (state, action: PayloadAction<OrgTestCountAnalytics | null>) => {
      state.orgTestCounts = action.payload;
    },
    setSingleDeviceTestCounts: (state, action: PayloadAction<TestCountAnalytics | null>) => {
      state.singleDeviceTestCounts = action.payload;
    },
    setAllDistributorIds: (state, action: PayloadAction<DistributorWithCountry[] | null>) => {
      state.allDistributorIds = action.payload;
    },
    removeAllDistributorIds: (state) => {
      state.allDistributorIds = null;
    },
  },
});

export const {
  resetAnalyticsState,
  requestDistributorSales,
  requestDistributorOrgTestCounts,
  requestCategoryDistribution,
  requestDemoTestTotals,
  requestDistributorComparison,
  requestMultiAccountDevices,
  requestOrgBreakdown,
  requestOrgTestCounts,
  requestSingleDeviceTestCounts,
  requestAllDistributorIds,
  setDistributorSales,
  setDistributorOrgTestCounts,
  setCategoryDistribution,
  setDemoTestTotals,
  setDistributorComparison,
  setMultiAccountDevices,
  setOrgBreakdown,
  clearOrgBreakdowns,
  setOrgTestCounts,
  setSingleDeviceTestCounts,
  setAllDistributorIds,
  removeAllDistributorIds,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
